import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import useStore from '../store';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PlanWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useStore();
  const [household, setHousehold] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [budgetType, setBudgetType] = useState('medium');
  const [customBudget, setCustomBudget] = useState('');
  const [guestsCount, setGuestsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const householdResponse = await axios.get(`${API}/households`, { withCredentials: true });
        const hh = householdResponse.data.household;
        setHousehold(hh);

        const membersResponse = await axios.get(`${API}/family-members/${hh.household_id}`, { withCredentials: true });
        setFamilyMembers(membersResponse.data.members);
        setSelectedMembers(membersResponse.data.members.map(m => m.member_id));
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load family data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleGeneratePlan = async () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one family member');
      return;
    }

    setGenerating(true);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];

      const planData = {
        household_id: household.household_id,
        start_date: startDate,
        family_member_ids: selectedMembers,
        budget_type: budgetType,
        custom_budget_week_eur: budgetType === 'custom' && customBudget ? parseFloat(customBudget) : null,
        guests_count: guestsCount
      };

      const response = await axios.post(`${API}/week-plans`, planData, { 
        withCredentials: true,
        timeout: 120000
      });
      
      toast.success('Weekly plan created!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Plan generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (familyMembers.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>No Family Members</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Please add family members first before creating a plan.</p>
            <Button onClick={() => navigate('/onboarding')} className="w-full" data-testid="go-to-onboarding-btn">
              Add Family Members
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-primary rounded-full p-3">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="plan-wizard-title">
            {t('plan.create_title')}
          </h1>
        </div>

        <div className="space-y-6">
          <Card data-testid="select-members-card">
            <CardHeader>
              <CardTitle>{t('plan.select_members')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {familyMembers.map((member) => (
                  <label 
                    key={member.member_id} 
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    data-testid={`member-checkbox-${member.member_id}`}
                  >
                    <Checkbox
                      checked={selectedMembers.includes(member.member_id)}
                      onCheckedChange={() => toggleMember(member.member_id)}
                    />
                    <div className="flex-1">
                      <p className="font-semibold">{member.name}</p>
                      <p className="text-sm text-gray-600">
                        {member.age} years • {member.religious_diet !== 'none' && `${member.religious_diet} • `}
                        {member.diet_tags.length > 0 && member.diet_tags.join(', ')}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="select-budget-card">
            <CardHeader>
              <CardTitle>{t('plan.select_budget')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={budgetType} onValueChange={setBudgetType}>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value="low" data-testid="budget-low" />
                    <div>
                      <p className="font-semibold">{t('plan.budget_low')}</p>
                      <p className="text-sm text-gray-600">Basic meals, cost-effective</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value="medium" data-testid="budget-medium" />
                    <div>
                      <p className="font-semibold">{t('plan.budget_medium')}</p>
                      <p className="text-sm text-gray-600">Balanced variety and quality</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value="high" data-testid="budget-high" />
                    <div>
                      <p className="font-semibold">{t('plan.budget_high')}</p>
                      <p className="text-sm text-gray-600">Premium ingredients, more variety</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value="unlimited" data-testid="budget-unlimited" />
                    <div>
                      <p className="font-semibold">{t('plan.budget_unlimited')}</p>
                      <p className="text-sm text-gray-600">No budget limits</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value="custom" data-testid="budget-custom" />
                    <div className="flex-1">
                      <p className="font-semibold mb-2">{t('plan.budget_custom')}</p>
                      {budgetType === 'custom' && (
                        <Input
                          type="number"
                          placeholder="Enter weekly budget in €"
                          value={customBudget}
                          onChange={(e) => setCustomBudget(e.target.value)}
                          data-testid="custom-budget-input"
                        />
                      )}
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="guests">{t('plan.guests')}</Label>
                <Input
                  id="guests"
                  type="number"
                  min="0"
                  value={guestsCount}
                  onChange={(e) => setGuestsCount(parseInt(e.target.value) || 0)}
                  data-testid="guests-input"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          <Button
            className="w-full bg-primary hover:bg-primary-hover text-white py-6 text-lg rounded-full font-semibold shadow-lg"
            onClick={handleGeneratePlan}
            disabled={generating || selectedMembers.length === 0}
            data-testid="generate-plan-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('plan.generating')} (This may take 30-60 seconds)
              </>
            ) : (
              t('plan.generate')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlanWizard;
